import { computeUnitPrice } from './computeUnitPrice';
import type { ConsumerMode } from './resolveModePrice';
import type { MenuItem, OptionChoice, OptionGroup, SelectedOption } from '@/types';

/**
 * SC-1739 — ajouter une option sur un plat déjà envoyé. resource_api reste seul
 * juge (`_quote_option_addons`) ; ces calculs ne servent qu'à ne proposer que ce
 * qu'il acceptera, avec la même règle, comptée sur la ligne entière :
 * `déjà porté + qté × plats ≤ max × qté de la ligne`.
 */

export interface SentLineOption {
  option_value_uuid?: string;
  qty?: number;
}

export interface SentLine {
  uuid: string;
  qte: number;
  options: SentLineOption[];
  addonOfItemUuid?: string | null;
  addonOptionUuid?: string | null;
}

/** optionId → quantité par plat choisie dans la fenêtre. */
export type AddonSelection = Record<string, number>;

/** Ce que la ligne porte déjà, par option : ses options × sa quantité, plus les
 *  ajouts déjà envoyés sur elle. */
export function carriedByLine(line: SentLine, allLines: SentLine[]): Map<string, number> {
  const carried = new Map<string, number>();
  const add = (id: string, qty: number) => carried.set(id, (carried.get(id) ?? 0) + qty);
  for (const option of line.options) {
    if (option.option_value_uuid) {
      add(option.option_value_uuid, Math.max(1, Number(option.qty) || 1) * line.qte);
    }
  }
  for (const other of allLines) {
    if (other.addonOfItemUuid === line.uuid && other.addonOptionUuid) {
      add(other.addonOptionUuid, other.qte);
    }
  }
  return carried;
}

function groupCap(group: OptionGroup): number {
  if (group.max && group.max > 0) return group.max;
  return group.multiple ? Number.POSITIVE_INFINITY : 1;
}

function carriedInGroup(group: OptionGroup, carried: Map<string, number>): number {
  return group.options.reduce((sum, option) => sum + (carried.get(option.id) ?? 0), 0);
}

/**
 * La quantité que cette option peut atteindre, par plat, avec le reste du choix.
 * `group` doit être le groupe ENTIER du plat, pas celui réduit par `addableGroups` :
 * une option retirée parce qu'elle est à son plafond compte toujours dans son groupe.
 */
export function maxPerPlate(
  group: OptionGroup,
  option: OptionChoice,
  args: {
    plates: number;
    plateCount: number;
    carried: Map<string, number>;
    selection: AddonSelection;
  },
): number {
  const { plates, plateCount, carried, selection } = args;
  const others = group.options
    .filter((other) => other.id !== option.id)
    .reduce((sum, other) => sum + (selection[other.id] ?? 0) * plates, 0);
  const groupRoom = groupCap(group) * plateCount - carriedInGroup(group, carried) - others;
  const optionRoom =
    option.maxQty != null
      ? option.maxQty * plateCount - (carried.get(option.id) ?? 0)
      : Number.POSITIVE_INFINITY;
  return Math.floor(Math.min(groupRoom, optionRoom) / plates);
}

/** Première quantité d'une option : `minQty` ne s'applique qu'à une option pas
 *  encore présente sur la ligne. */
export function firstQty(option: OptionChoice, carried: Map<string, number>): number {
  return (carried.get(option.id) ?? 0) > 0 ? 1 : Math.max(1, option.minQty ?? 0);
}

/** Les groupes encore ouverts, réduits aux options qui ont encore de la place sur
 *  un plat. Le montant négatif est refusé par le devis : on ne le propose pas. */
export function addableGroups(
  product: MenuItem,
  plateCount: number,
  carried: Map<string, number>,
): OptionGroup[] {
  return (product.optionGroups ?? [])
    .map((group) => ({
      ...group,
      options: group.options.filter(
        (option) =>
          option.priceDelta >= 0 &&
          maxPerPlate(group, option, { plates: 1, plateCount, carried, selection: {} }) >=
            firstQty(option, carried),
      ),
    }))
    .filter((group) => group.options.length > 0);
}

/** Le choix ramené à ce qui tient encore quand le nombre de plats change.
 *  `groups` : les groupes entiers du plat, comme pour `maxPerPlate`. */
export function clampSelection(
  groups: OptionGroup[],
  selection: AddonSelection,
  args: { plates: number; plateCount: number; carried: Map<string, number> },
): AddonSelection {
  const next: AddonSelection = {};
  for (const group of groups) {
    for (const option of group.options) {
      const qty = selection[option.id] ?? 0;
      if (qty <= 0) continue;
      const allowed = maxPerPlate(group, option, { ...args, selection: next });
      const kept = Math.min(qty, allowed);
      if (kept >= firstQty(option, args.carried)) next[option.id] = kept;
    }
  }
  return next;
}

/** Le supplément d'une unité de l'option : le même calcul que le panier, buffet
 *  et prix par mode compris. */
export function optionUnitCents(
  product: MenuItem,
  group: OptionGroup,
  option: OptionChoice,
  isBuffetDineIn: boolean,
  mode: ConsumerMode,
): number {
  const groups = product.optionGroups ?? [];
  const selected: SelectedOption[] = [{ groupId: group.id, options: [{ id: option.id, qty: 1 }] }];
  return (
    computeUnitPrice(product, groups, selected, isBuffetDineIn, mode) -
    computeUnitPrice(product, groups, [], isBuffetDineIn, mode)
  );
}
